import type { UrlObject } from 'url';

import { joinIds } from '@/clientOnlyUtils/clientUtils';
import type { RouterOutputs } from '@/utils/api';

export type GlobalSearchResults = RouterOutputs['search']['global'];
export type SearchGroupKey = keyof GlobalSearchResults;
/** 'all' shows every group; a group key narrows the popover to that group. */
export type SearchFacet = 'all' | SearchGroupKey;

/** Fewer usable characters than this never searches (the server rejects them too). */
export const MIN_SEARCH_CHARS = 2;

/**
 * The query as the search will see it: the characters full-text search treats as operators
 * (`!'()|&:*`) stripped — the same set the server's sanitizer drops. "::" has nothing left to search.
 */
export const usableSearchText = (query: string) => query.replace(/[!'()|&:*]/g, '').trim();

/** Whether the box holds something worth searching (and offering "Search all people" for). */
export const isSearchable = (query: string) => usableSearchText(query).length >= MIN_SEARCH_CHARS;
export const SEARCH_DEBOUNCE_MS = 200;

/** Group order and labels, matching the server's group order. */
export const SEARCH_GROUPS: { key: SearchGroupKey; label: string }[] = [
  { key: 'people', label: 'People' },
  { key: 'firms', label: 'Firms' },
  { key: 'campaigns', label: 'Campaigns' },
  { key: 'snippets', label: 'Snippets' },
  { key: 'content', label: 'Content' },
];

/** Things to DO from the search box, not records to open. Matched on the client by keyword. */
export type QuickAction = { id: string; label: string; description: string; href: UrlObject; keywords: string[] };

export const QUICK_ACTIONS: QuickAction[] = [
  {
    id: 'action:import-csv',
    label: 'Import a CSV',
    description: 'Add contacts from a spreadsheet, with tags',
    href: { pathname: '/uploadcsv' },
    keywords: ['import', 'upload', 'csv', 'spreadsheet', 'add', 'contacts', 'people', 'leads'],
  },
];

export const SEE_ALL_ID = 'see-all';

export type SearchItem =
  | { kind: 'people'; id: string; data: GlobalSearchResults['people'][number] }
  | { kind: 'firms'; id: string; data: GlobalSearchResults['firms'][number] }
  | { kind: 'campaigns'; id: string; data: GlobalSearchResults['campaigns'][number] }
  | { kind: 'snippets'; id: string; data: GlobalSearchResults['snippets'][number] }
  | { kind: 'content'; id: string; data: GlobalSearchResults['content'][number] }
  | { kind: 'action'; id: string; data: QuickAction }
  /** A group's "All …" link (its full list, filtered by the query) — an option like any row, so ↑/↓ reach it. */
  | { kind: 'groupAll'; id: string; group: SearchGroupKey }
  | { kind: 'seeAll'; id: typeof SEE_ALL_ID };

export type SearchSection =
  | { key: SearchGroupKey; label: string; items: SearchItem[] }
  | { key: 'actions'; label: string; items: SearchItem[] };

const itemsFor = (results: GlobalSearchResults, key: SearchGroupKey): SearchItem[] => {
  switch (key) {
    case 'people':
      return results.people.map((data) => ({ kind: 'people', id: `people:${data.id}`, data }));
    case 'firms':
      return results.firms.map((data) => ({ kind: 'firms', id: `firms:${data.id}`, data }));
    case 'campaigns':
      return results.campaigns.map((data) => ({ kind: 'campaigns', id: `campaigns:${data.id}`, data }));
    case 'snippets':
      return results.snippets.map((data) => ({ kind: 'snippets', id: `snippets:${data.id}`, data }));
    case 'content':
      return results.content.map((data) => ({ kind: 'content', id: `content:${data.id}`, data }));
  }
};

/** Quick actions for the typed text: all of them before a real query, else those a word prefix-matches. */
export function matchQuickActions(query: string): QuickAction[] {
  if (!isSearchable(query)) return QUICK_ACTIONS;
  const words = usableSearchText(query).toLowerCase().split(/\s+/).filter((w) => w.length > 0);
  return QUICK_ACTIONS.filter((a) =>
    words.some((w) => [...a.keywords, ...a.label.toLowerCase().split(/\s+/)].some((k) => k.startsWith(w))),
  );
}

/**
 * The popover's sections and its flat, keyboard-ordered item list.
 *  - Before a real query: only the quick actions (nothing to search yet).
 *  - With a query: the result groups for the selected facet (empty groups dropped), each led by its
 *    "All …" option (it sits in the group header), then matching quick actions (on "All"), then
 *    "Search all people", which is always last and always there.
 * `results` must already be the answer for `query` — the caller drops stale answers.
 */
export function buildSearchView(
  results: GlobalSearchResults | undefined,
  facet: SearchFacet,
  query: string,
): { sections: SearchSection[]; items: SearchItem[]; counts: Record<SearchGroupKey, number> } {
  const counts: Record<SearchGroupKey, number> = { people: 0, firms: 0, campaigns: 0, snippets: 0, content: 0 };
  const longEnough = isSearchable(query);
  const sections: SearchSection[] = [];
  if (results != null && longEnough) {
    for (const group of SEARCH_GROUPS) {
      const groupItems = itemsFor(results, group.key);
      counts[group.key] = groupItems.length;
      if (groupItems.length === 0) continue;
      if (facet !== 'all' && facet !== group.key) continue;
      sections.push({ key: group.key, label: group.label, items: groupItems });
    }
  }
  const actions = facet === 'all' ? matchQuickActions(query) : [];
  if (actions.length > 0) {
    sections.push({
      key: 'actions',
      label: 'Actions',
      items: actions.map((data) => ({ kind: 'action', id: data.id, data })),
    });
  }
  const items: SearchItem[] = sections.flatMap((s): SearchItem[] =>
    s.key === 'actions' ? s.items : [{ kind: 'groupAll', id: groupAllId(s.key), group: s.key }, ...s.items],
  );
  if (longEnough) items.push({ kind: 'seeAll', id: SEE_ALL_ID });
  return { sections, items, counts };
}

export const groupAllId = (key: SearchGroupKey) => `group-all:${key}`;

/** The row Enter opens when nothing is arrowed to: the first actual result, not a group's "All …" link. */
export const defaultItem = (items: SearchItem[]) => items.find((it) => it.kind !== 'groupAll');

/**
 * Next active row for ↑/↓, wrapping around. With nothing active, ↓ picks the first result (skipping the
 * first group's "All …" link, which ↑ from there still reaches) and ↑ the last row.
 */
export function moveActive(items: SearchItem[], activeId: string | null, delta: 1 | -1): string | null {
  if (items.length === 0) return null;
  const i = items.findIndex((it) => it.id === activeId);
  if (i === -1) return (delta === 1 ? defaultItem(items) : items[items.length - 1])?.id ?? null;
  return items[(i + delta + items.length) % items.length]?.id ?? null;
}

/**
 * The full list behind each group, filtered by the query, using each page's own `q` search param.
 * Firms have no list page of their own: theirs opens the people search on the `org:` prefix (contacts
 * at a matching firm), which the contact search supports.
 */
export function groupSeeAllHref(key: SearchGroupKey, query: string): UrlObject {
  const q = query.trim();
  switch (key) {
    case 'people':
      return { pathname: '/prospects', query: { q } };
    case 'firms':
      return { pathname: '/prospects', query: { q: `org:${q}` } };
    case 'campaigns':
      return { pathname: '/templates', query: { q } };
    case 'snippets':
      return { pathname: '/prompts', query: { q } };
    case 'content':
      return { pathname: '/content', query: { q } };
  }
}

export const GROUP_SEE_ALL_LABEL: Record<SearchGroupKey, string> = {
  people: 'All people',
  firms: 'People at these firms',
  campaigns: 'All campaigns',
  snippets: 'All snippets',
  content: 'All content',
};

/**
 * Where a row goes. People and firms open in the /prospects detail column over the full search for the
 * same query — the exact params a /prospects card sets (`openContactDetailQuery`) — so closing the panel
 * leaves the user on the full results instead of an empty page.
 */
export function searchItemHref(item: SearchItem, query: string): UrlObject {
  const q = query.trim();
  switch (item.kind) {
    case 'people':
      return {
        pathname: '/prospects',
        query: {
          q,
          id: joinIds({ contactId: item.data.id, cadenceId: item.data.cadenceId ?? undefined }),
          detailType: 'interact',
        },
      };
    case 'firms':
      return { pathname: '/prospects', query: { q, orgId: item.data.id } };
    case 'campaigns':
      return { pathname: `/templates/${item.data.id}` };
    case 'snippets':
      return { pathname: `/prompts/${item.data.id}` };
    case 'content':
      return { pathname: '/content', query: { id: item.data.id } };
    case 'action':
      return item.data.href;
    case 'groupAll':
      return groupSeeAllHref(item.group, q);
    case 'seeAll':
      return groupSeeAllHref('people', q);
  }
}
